import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar as CalendarIcon, Plus, Pencil, Trash2, Clock, Ban, Link as LinkIcon,
  Copy, Check, ChevronRight, User
} from 'lucide-react';
import apiClient from '../../utils/apiClient';
import CustomAlert from './shared/CustomAlert';

// ⭐ Módulo de Calendarios (pedido de Angelo, 2026-08-07: "módulo de
// Calendarios completo estilo GHL", con página pública de reserva).
// Decisiones de scope confirmadas con Angelo antes de construir esto:
//  - Disponibilidad POR EMPLEADO, no un horario general del negocio -- cada
//    calendario tiene su propio dueño de agenda (employee_id, NULL = el
//    dueño del negocio).
//  - Al reservar desde la página pública se dispara un trigger nuevo de
//    Workflows ('appointment_booked') -- ver backend/index.js y
//    workflowEngine.js para armar automatizaciones sobre una cita nueva
//    (confirmación por WhatsApp, recordatorio, etc.) igual que con
//    cualquier otro trigger.
//
// PUT /api/pos/crm/booking-config está gateado requireOwner en el backend
// (es la URL pública de TODO el negocio, no una preferencia de un
// calendario) -- a propósito NO se replica ese gate acá en el frontend
// (nada de "isOwner &&..."): si un empleado con permiso 'crm' intenta
// guardarlo, el backend devuelve 403 con su propio mensaje y se muestra
// tal cual en el CustomAlert de error. Mismo criterio que dejó fix #261
// ("los permisos deben depender solo del backend, no de cache local").
//
// Mismo patrón de horario semanal en TODO el proyecto: un solo bloque por
// día (mañana Y tarde en un rango, sin "hueco de almuerzo" en v1) -- el
// backend sí soporta múltiples bloques por día_of_week, pero la UI se
// simplifica a propósito para no complicar el primer uso; si Angelo pide
// horarios partidos más adelante, es agregar un botón "+" por día sin
// tocar el backend.
const DAYS = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 0, label: 'Domingo' }
];

const CALENDAR_COLOR_SWATCHES = ['#1e3a8a', '#8b5cf6', '#f59e0b', '#f97316', '#22c55e', '#ef4444', '#ec4899', '#0ea5e9'];

const EMPTY_CALENDAR_FORM = {
  name: '', slug: '', employee_id: '', description: '',
  duration_minutes: 30, buffer_minutes: 0, advance_notice_hours: 1,
  booking_window_days: 30, color: CALENDAR_COLOR_SWATCHES[0], is_active: true
};

const APPOINTMENT_STATUS_META = {
  confirmed: { label: 'Confirmada', className: 'bg-blue-100 text-blue-700' },
  completed: { label: 'Completada', className: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelada', className: 'bg-red-100 text-red-700' },
  no_show: { label: 'No asistió', className: 'bg-gray-200 text-gray-600' }
};

function slugify(text) {
  return String(text || '')
    .toLowerCase()
    // Quita tildes (á->a, ñ se pierde en n, aceptable para un slug) antes
    // de descartar cualquier caracter que no sea a-z0-9 -- así "Peluquería"
    // da "peluqueria" en vez de "peluquer-a".
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

const CalendariosManager = () => {
  const [section, setSection] = useState('calendarios'); // 'calendarios' | 'citas'
  const [customAlert, setCustomAlert] = useState(null);
  const showAlert = (type, title, message) => setCustomAlert({ type, title, message });

  // ── booking-config ──
  const [bookingSlug, setBookingSlug] = useState('');
  const [savedBookingSlug, setSavedBookingSlug] = useState('');
  const [savingSlug, setSavingSlug] = useState(false);
  const [copied, setCopied] = useState(false);

  // ── calendarios ──
  const [calendars, setCalendars] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loadingCalendars, setLoadingCalendars] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CALENDAR_FORM);
  const [selectedCalendarId, setSelectedCalendarId] = useState(null);
  const [detailTab, setDetailTab] = useState('datos'); // 'datos' | 'horario' | 'bloqueos'
  const [editForm, setEditForm] = useState(EMPTY_CALENDAR_FORM);
  const [savingCalendar, setSavingCalendar] = useState(false);

  // ── horario semanal ──
  const [weeklyBlocks, setWeeklyBlocks] = useState({}); // { [day_of_week]: { enabled, start_time, end_time } }
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [savingAvailability, setSavingAvailability] = useState(false);

  // ── bloqueos ──
  const [timeOff, setTimeOff] = useState([]);
  const [loadingTimeOff, setLoadingTimeOff] = useState(false);
  const [newTimeOff, setNewTimeOff] = useState({ date: '', start_time: '', end_time: '', reason: '' });

  // ── citas ──
  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [appointmentFilter, setAppointmentFilter] = useState({ calendar_id: '', status: '' });

  const selectedCalendar = calendars.find(c => c.id === selectedCalendarId) || null;

  const loadBookingConfig = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/pos/crm/booking-config');
      setBookingSlug(res.data?.booking_slug || '');
      setSavedBookingSlug(res.data?.booking_slug || '');
    } catch (error) {
      // silencioso -- si esto falla, el input simplemente queda vacío
    }
  }, []);

  const loadCalendars = useCallback(async () => {
    setLoadingCalendars(true);
    try {
      const res = await apiClient.get('/api/pos/crm/calendars');
      setCalendars(res.data || []);
    } catch (error) {
      showAlert('error', 'Error', 'No se pudieron cargar los calendarios');
    } finally {
      setLoadingCalendars(false);
    }
  }, []);

  const loadEmployees = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/pos/employees');
      setEmployees(res.data || []);
    } catch (error) {
      // ⭐ requireOwner en el backend -- un empleado con permiso 'crm' que
      // entra a este módulo recibe 403 acá y no ve la lista de compañeros
      // para asignar como dueños de calendario. No es un error real: se
      // ignora en silencio y el selector queda con solo la opción "Dueño".
      setEmployees([]);
    }
  }, []);

  useEffect(() => {
    loadBookingConfig();
    loadCalendars();
    loadEmployees();
  }, [loadBookingConfig, loadCalendars, loadEmployees]);

  useEffect(() => {
    if (selectedCalendar) {
      setEditForm({
        name: selectedCalendar.name || '',
        slug: selectedCalendar.slug || '',
        employee_id: selectedCalendar.employee_id || '',
        description: selectedCalendar.description || '',
        duration_minutes: selectedCalendar.duration_minutes || 30,
        buffer_minutes: selectedCalendar.buffer_minutes || 0,
        advance_notice_hours: selectedCalendar.advance_notice_hours || 1,
        booking_window_days: selectedCalendar.booking_window_days || 30,
        color: selectedCalendar.color || CALENDAR_COLOR_SWATCHES[0],
        is_active: !!selectedCalendar.is_active
      });
      setDetailTab('datos');
    }
  }, [selectedCalendarId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAvailability = useCallback(async (calendarId) => {
    setLoadingAvailability(true);
    try {
      const res = await apiClient.get(`/api/pos/crm/calendars/${calendarId}/availability`);
      const map = {};
      (res.data || []).forEach(row => {
        map[row.day_of_week] = { enabled: true, start_time: row.start_time, end_time: row.end_time };
      });
      setWeeklyBlocks(map);
    } catch (error) {
      showAlert('error', 'Error', 'No se pudo cargar el horario');
    } finally {
      setLoadingAvailability(false);
    }
  }, []);

  const loadTimeOff = useCallback(async (calendarId) => {
    setLoadingTimeOff(true);
    try {
      const res = await apiClient.get(`/api/pos/crm/calendars/${calendarId}/time-off`);
      setTimeOff(res.data || []);
    } catch (error) {
      showAlert('error', 'Error', 'No se pudieron cargar los bloqueos');
    } finally {
      setLoadingTimeOff(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCalendarId && detailTab === 'horario') loadAvailability(selectedCalendarId);
    if (selectedCalendarId && detailTab === 'bloqueos') loadTimeOff(selectedCalendarId);
  }, [selectedCalendarId, detailTab, loadAvailability, loadTimeOff]);

  const loadAppointments = useCallback(async () => {
    setLoadingAppointments(true);
    try {
      const params = {};
      if (appointmentFilter.calendar_id) params.calendar_id = appointmentFilter.calendar_id;
      if (appointmentFilter.status) params.status = appointmentFilter.status;
      const res = await apiClient.get('/api/pos/crm/appointments', { params });
      setAppointments(res.data || []);
    } catch (error) {
      showAlert('error', 'Error', 'No se pudieron cargar las citas');
    } finally {
      setLoadingAppointments(false);
    }
  }, [appointmentFilter]);

  useEffect(() => {
    if (section === 'citas') loadAppointments();
  }, [section, loadAppointments]);

  // ── booking-config ──
  const handleSaveBookingSlug = async () => {
    const slug = slugify(bookingSlug);
    if (!slug) return showAlert('error', 'Slug inválido', 'Escribe un slug válido para tu página de reservas.');
    setSavingSlug(true);
    try {
      await apiClient.put('/api/pos/crm/booking-config', { booking_slug: slug });
      setBookingSlug(slug);
      setSavedBookingSlug(slug);
      showAlert('success', 'Guardado', 'Tu página de reservas fue actualizada.');
    } catch (error) {
      showAlert('error', 'Error', error.response?.data?.error || 'No se pudo guardar el slug');
    } finally {
      setSavingSlug(false);
    }
  };

  const publicUrl = savedBookingSlug ? `${window.location.origin}/reservar/${savedBookingSlug}` : '';
  const handleCopyLink = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // ── crear calendario ──
  const handleCreateCalendar = async () => {
    const name = createForm.name.trim();
    const slug = slugify(createForm.slug || createForm.name);
    if (!name) return showAlert('error', 'Falta el nombre', 'Ponle un nombre al calendario.');
    if (!slug) return showAlert('error', 'Falta el slug', 'El slug no puede quedar vacío.');
    setSavingCalendar(true);
    try {
      await apiClient.post('/api/pos/crm/calendars', { ...createForm, name, slug, employee_id: createForm.employee_id || null });
      setShowCreateModal(false);
      setCreateForm(EMPTY_CALENDAR_FORM);
      loadCalendars();
    } catch (error) {
      showAlert('error', 'Error', error.response?.data?.error || 'No se pudo crear el calendario');
    } finally {
      setSavingCalendar(false);
    }
  };

  // ── editar datos básicos ──
  const handleSaveCalendarInfo = async () => {
    const name = editForm.name.trim();
    const slug = slugify(editForm.slug || editForm.name);
    if (!name) return showAlert('error', 'Falta el nombre', 'Ponle un nombre al calendario.');
    setSavingCalendar(true);
    try {
      await apiClient.put(`/api/pos/crm/calendars/${selectedCalendarId}`, { ...editForm, name, slug, employee_id: editForm.employee_id || null });
      loadCalendars();
      showAlert('success', 'Guardado', 'Los datos del calendario se actualizaron.');
    } catch (error) {
      showAlert('error', 'Error', error.response?.data?.error || 'No se pudo guardar');
    } finally {
      setSavingCalendar(false);
    }
  };

  const handleDeleteCalendar = (calendar) => {
    setCustomAlert({
      type: 'confirm',
      title: '¿Eliminar este calendario?',
      message: `"${calendar.name}" se va a borrar junto con su horario y bloqueos. Si tiene citas confirmadas a futuro, no se podrá borrar -- desactívalo en vez de eso.`,
      onConfirm: async () => {
        try {
          await apiClient.delete(`/api/pos/crm/calendars/${calendar.id}`);
          if (selectedCalendarId === calendar.id) setSelectedCalendarId(null);
          loadCalendars();
        } catch (error) {
          showAlert('error', 'Error', error.response?.data?.error || 'No se pudo eliminar el calendario');
        }
      }
    });
  };

  // ── horario semanal ──
  const toggleDay = (dayValue) => {
    setWeeklyBlocks(prev => {
      const current = prev[dayValue];
      if (current?.enabled) {
        const { [dayValue]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [dayValue]: { enabled: true, start_time: '09:00', end_time: '18:00' } };
    });
  };

  const updateDayTime = (dayValue, field, value) => {
    setWeeklyBlocks(prev => ({ ...prev, [dayValue]: { ...prev[dayValue], [field]: value } }));
  };

  const handleSaveAvailability = async () => {
    const blocks = Object.entries(weeklyBlocks)
      .filter(([, v]) => v.enabled)
      .map(([day_of_week, v]) => ({ day_of_week: Number(day_of_week), start_time: v.start_time, end_time: v.end_time }));

    for (const b of blocks) {
      if (!b.start_time || !b.end_time || b.start_time >= b.end_time) {
        return showAlert('error', 'Horario inválido', 'Revisa que cada día tenga una hora de inicio menor a la de fin.');
      }
    }

    setSavingAvailability(true);
    try {
      await apiClient.put(`/api/pos/crm/calendars/${selectedCalendarId}/availability`, { blocks });
      showAlert('success', 'Guardado', 'El horario semanal fue actualizado.');
    } catch (error) {
      showAlert('error', 'Error', error.response?.data?.error || 'No se pudo guardar el horario');
    } finally {
      setSavingAvailability(false);
    }
  };

  // ── bloqueos ──
  const handleAddTimeOff = async () => {
    if (!newTimeOff.date) return showAlert('error', 'Falta la fecha', 'Elige una fecha para el bloqueo.');
    try {
      await apiClient.post(`/api/pos/crm/calendars/${selectedCalendarId}/time-off`, newTimeOff);
      setNewTimeOff({ date: '', start_time: '', end_time: '', reason: '' });
      loadTimeOff(selectedCalendarId);
    } catch (error) {
      showAlert('error', 'Error', error.response?.data?.error || 'No se pudo crear el bloqueo');
    }
  };

  const handleDeleteTimeOff = async (id) => {
    try {
      await apiClient.delete(`/api/pos/crm/time-off/${id}`);
      loadTimeOff(selectedCalendarId);
    } catch (error) {
      showAlert('error', 'Error', 'No se pudo eliminar el bloqueo');
    }
  };

  // ── citas ──
  const handleChangeAppointmentStatus = async (appointment, status) => {
    try {
      await apiClient.patch(`/api/pos/crm/appointments/${appointment.id}`, { status });
      loadAppointments();
    } catch (error) {
      showAlert('error', 'Error', 'No se pudo actualizar la cita');
    }
  };

  const formatAppointmentDate = (sqlDateTime) => {
    // sqlDateTime viene 'YYYY-MM-DD HH:MM:SS' en UTC (sin sufijo) -- mismo
    // criterio que parseSqliteUtc() del backend.
    const d = new Date(String(sqlDateTime).replace(' ', 'T') + 'Z');
    if (isNaN(d.getTime())) return sqlDateTime;
    return d.toLocaleString('es-EC', { timeZone: 'America/Guayaquil', dateStyle: 'medium', timeStyle: 'short' });
  };

  return (
    <div className="space-y-6">
      {customAlert && (
        <CustomAlert
          type={customAlert.type}
          title={customAlert.title}
          message={customAlert.message}
          onClose={() => setCustomAlert(null)}
          onConfirm={customAlert.onConfirm}
          zIndex={50}
        />
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <CalendarIcon className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Calendarios</h1>
            <p className="text-sm text-gray-600">Agenda por empleado y página pública de reservas.</p>
          </div>
        </div>
        <div className="flex gap-2 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setSection('calendarios')}
            className={`px-4 py-1.5 rounded-md text-sm font-semibold transition ${section === 'calendarios' ? 'bg-white shadow text-blue-600' : 'text-gray-500'}`}
          >
            Calendarios
          </button>
          <button
            onClick={() => setSection('citas')}
            className={`px-4 py-1.5 rounded-md text-sm font-semibold transition ${section === 'citas' ? 'bg-white shadow text-blue-600' : 'text-gray-500'}`}
          >
            Citas
          </button>
        </div>
      </div>

      {section === 'calendarios' && (
        <>
          {/* Página pública */}
          <div className="bg-white rounded-lg shadow p-5 space-y-3">
            <div className="flex items-center gap-2">
              <LinkIcon className="w-4 h-4 text-gray-400" />
              <h2 className="font-semibold text-gray-800">Página pública de reservas</h2>
            </div>
            <p className="text-xs text-gray-500">
              Elige la dirección donde tus clientes van a poder reservar. Solo el dueño de la cuenta puede cambiarla.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-400 whitespace-nowrap">{window.location.origin}/reservar/</span>
              <input
                type="text"
                value={bookingSlug}
                onChange={(e) => setBookingSlug(e.target.value.toLowerCase())}
                placeholder="mi-negocio"
                className="flex-1 min-w-[140px] px-3 py-2 border rounded-lg text-sm"
              />
              <button
                onClick={handleSaveBookingSlug}
                disabled={savingSlug}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
              >
                {savingSlug ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
            {publicUrl && (
              <div className="flex items-center gap-2 text-sm">
                <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{publicUrl}</a>
                <button onClick={handleCopyLink} className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 flex-shrink-0">
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Lista de calendarios */}
            <div className="lg:col-span-1 bg-white rounded-lg shadow p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-gray-800">Tus calendarios</h2>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700"
                >
                  <Plus className="w-3.5 h-3.5" /> Nuevo
                </button>
              </div>
              {loadingCalendars ? (
                <p className="text-sm text-gray-400">Cargando...</p>
              ) : calendars.length === 0 ? (
                <p className="text-sm text-gray-400 italic">Todavía no creaste ningún calendario.</p>
              ) : (
                <div className="space-y-2">
                  {calendars.map(cal => (
                    <button
                      key={cal.id}
                      onClick={() => setSelectedCalendarId(cal.id)}
                      className={`w-full text-left border rounded-lg p-3 transition ${selectedCalendarId === cal.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: cal.color }} />
                          <span className="font-medium text-gray-800 truncate">{cal.name}</span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                        <User className="w-3 h-3" />
                        <span>{cal.employee_name || 'Dueño del negocio'}</span>
                        <span>·</span>
                        <Clock className="w-3 h-3" />
                        <span>{cal.duration_minutes} min</span>
                        {!cal.is_active && <span className="ml-auto text-gray-400 italic">Inactivo</span>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Panel de detalle */}
            <div className="lg:col-span-2 bg-white rounded-lg shadow p-5">
              {!selectedCalendar ? (
                <div className="h-full flex items-center justify-center text-sm text-gray-400 italic py-12">
                  Elige un calendario de la izquierda para ver su horario y bloqueos.
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex gap-2 bg-gray-100 rounded-lg p-1">
                      {['datos', 'horario', 'bloqueos'].map(tab => (
                        <button
                          key={tab}
                          onClick={() => setDetailTab(tab)}
                          className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize transition ${detailTab === tab ? 'bg-white shadow text-blue-600' : 'text-gray-500'}`}
                        >
                          {tab === 'horario' ? 'Horario semanal' : tab}
                        </button>
                      ))}
                    </div>
                    <button onClick={() => handleDeleteCalendar(selectedCalendar)} className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {detailTab === 'datos' && (
                    <div className="space-y-3 max-w-lg">
                      <div>
                        <label className="text-xs font-semibold text-gray-500">Nombre</label>
                        <input type="text" value={editForm.name} onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-1" />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500">Slug (usado en la URL pública)</label>
                        <input type="text" value={editForm.slug} onChange={(e) => setEditForm(f => ({ ...f, slug: e.target.value.toLowerCase() }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-1" />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500">Empleado dueño de esta agenda</label>
                        <select value={editForm.employee_id} onChange={(e) => setEditForm(f => ({ ...f, employee_id: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-1">
                          <option value="">Dueño del negocio</option>
                          {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500">Descripción (opcional, se muestra en la página pública)</label>
                        <textarea value={editForm.description} onChange={(e) => setEditForm(f => ({ ...f, description: e.target.value }))} rows={2} className="w-full px-3 py-2 border rounded-lg text-sm mt-1" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-semibold text-gray-500">Duración (min)</label>
                          <input type="number" min="5" max="480" value={editForm.duration_minutes} onChange={(e) => setEditForm(f => ({ ...f, duration_minutes: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-1" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-500">Buffer entre citas (min)</label>
                          <input type="number" min="0" max="120" value={editForm.buffer_minutes} onChange={(e) => setEditForm(f => ({ ...f, buffer_minutes: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-1" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-500">Aviso mínimo (horas)</label>
                          <input type="number" min="0" max="168" value={editForm.advance_notice_hours} onChange={(e) => setEditForm(f => ({ ...f, advance_notice_hours: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-1" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-500">Ventana de reserva (días)</label>
                          <input type="number" min="1" max="365" value={editForm.booking_window_days} onChange={(e) => setEditForm(f => ({ ...f, booking_window_days: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-1" />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500">Color</label>
                        <div className="flex gap-1.5 mt-1">
                          {CALENDAR_COLOR_SWATCHES.map(c => (
                            <button key={c} onClick={() => setEditForm(f => ({ ...f, color: c }))} style={{ backgroundColor: c }} className={`w-5 h-5 rounded-full ${editForm.color === c ? 'ring-2 ring-offset-1 ring-gray-500' : ''}`} />
                          ))}
                        </div>
                      </div>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={editForm.is_active} onChange={(e) => setEditForm(f => ({ ...f, is_active: e.target.checked }))} />
                        Activo (visible en la página pública)
                      </label>
                      <button onClick={handleSaveCalendarInfo} disabled={savingCalendar} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                        {savingCalendar ? 'Guardando...' : 'Guardar cambios'}
                      </button>
                    </div>
                  )}

                  {detailTab === 'horario' && (
                    loadingAvailability ? <p className="text-sm text-gray-400">Cargando...</p> : (
                      <div className="space-y-3 max-w-lg">
                        {DAYS.map(day => {
                          const block = weeklyBlocks[day.value];
                          return (
                            <div key={day.value} className="flex items-center gap-3 border rounded-lg p-2.5">
                              <label className="flex items-center gap-2 w-28 flex-shrink-0 text-sm font-medium">
                                <input type="checkbox" checked={!!block?.enabled} onChange={() => toggleDay(day.value)} />
                                {day.label}
                              </label>
                              {block?.enabled ? (
                                <div className="flex items-center gap-2 text-sm">
                                  <input type="time" value={block.start_time} onChange={(e) => updateDayTime(day.value, 'start_time', e.target.value)} className="px-2 py-1 border rounded-lg text-sm" />
                                  <span className="text-gray-400">a</span>
                                  <input type="time" value={block.end_time} onChange={(e) => updateDayTime(day.value, 'end_time', e.target.value)} className="px-2 py-1 border rounded-lg text-sm" />
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400 italic">Cerrado</span>
                              )}
                            </div>
                          );
                        })}
                        <button onClick={handleSaveAvailability} disabled={savingAvailability} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                          {savingAvailability ? 'Guardando...' : 'Guardar horario'}
                        </button>
                      </div>
                    )
                  )}

                  {detailTab === 'bloqueos' && (
                    <div className="space-y-4 max-w-lg">
                      <div className="border rounded-lg p-3 bg-gray-50 space-y-2">
                        <p className="text-xs font-semibold text-gray-500 flex items-center gap-1"><Ban className="w-3.5 h-3.5" /> Nuevo bloqueo</p>
                        <div className="grid grid-cols-3 gap-2">
                          <input type="date" value={newTimeOff.date} onChange={(e) => setNewTimeOff(f => ({ ...f, date: e.target.value }))} className="px-2 py-1.5 border rounded-lg text-sm col-span-1" />
                          <input type="time" value={newTimeOff.start_time} onChange={(e) => setNewTimeOff(f => ({ ...f, start_time: e.target.value }))} placeholder="Desde (opcional)" className="px-2 py-1.5 border rounded-lg text-sm" />
                          <input type="time" value={newTimeOff.end_time} onChange={(e) => setNewTimeOff(f => ({ ...f, end_time: e.target.value }))} placeholder="Hasta (opcional)" className="px-2 py-1.5 border rounded-lg text-sm" />
                        </div>
                        <p className="text-xs text-gray-400">Deja las horas vacías para bloquear el día completo.</p>
                        <input type="text" value={newTimeOff.reason} onChange={(e) => setNewTimeOff(f => ({ ...f, reason: e.target.value }))} placeholder="Motivo (opcional)" className="w-full px-2 py-1.5 border rounded-lg text-sm" />
                        <button onClick={handleAddTimeOff} className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700">Agregar bloqueo</button>
                      </div>
                      {loadingTimeOff ? <p className="text-sm text-gray-400">Cargando...</p> : timeOff.length === 0 ? (
                        <p className="text-sm text-gray-400 italic">Sin bloqueos próximos.</p>
                      ) : (
                        <div className="space-y-1.5">
                          {timeOff.map(t => (
                            <div key={t.id} className="flex items-center justify-between gap-2 border rounded-lg p-2.5 text-sm">
                              <div>
                                <span className="font-medium">{t.date}</span>
                                <span className="text-gray-400 ml-2">{t.start_time && t.end_time ? `${t.start_time} - ${t.end_time}` : 'Todo el día'}</span>
                                {t.reason && <span className="text-gray-400 ml-2 italic">"{t.reason}"</span>}
                              </div>
                              <button onClick={() => handleDeleteTimeOff(t.id)} className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {section === 'citas' && (
        <div className="bg-white rounded-lg shadow p-5 space-y-4">
          <div className="flex flex-wrap gap-2">
            <select value={appointmentFilter.calendar_id} onChange={(e) => setAppointmentFilter(f => ({ ...f, calendar_id: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm">
              <option value="">Todos los calendarios</option>
              {calendars.map(cal => <option key={cal.id} value={cal.id}>{cal.name}</option>)}
            </select>
            <select value={appointmentFilter.status} onChange={(e) => setAppointmentFilter(f => ({ ...f, status: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm">
              <option value="">Todos los estados</option>
              {Object.entries(APPOINTMENT_STATUS_META).map(([value, meta]) => <option key={value} value={value}>{meta.label}</option>)}
            </select>
          </div>

          {loadingAppointments ? (
            <p className="text-sm text-gray-400">Cargando...</p>
          ) : appointments.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No hay citas para este filtro.</p>
          ) : (
            <div className="space-y-2">
              {appointments.map(appt => {
                const meta = APPOINTMENT_STATUS_META[appt.status] || APPOINTMENT_STATUS_META.confirmed;
                return (
                  <div key={appt.id} className="border rounded-lg p-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: appt.calendar_color || '#1e3a8a' }} />
                        <span className="font-semibold text-gray-800">{appt.customer_name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${meta.className}`}>{meta.label}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {appt.calendar_name} {appt.employee_name ? `· ${appt.employee_name}` : ''} · {formatAppointmentDate(appt.start_at)}
                      </p>
                      <p className="text-xs text-gray-400">{appt.customer_phone}{appt.customer_email ? ` · ${appt.customer_email}` : ''}</p>
                      {appt.notes && <p className="text-xs text-gray-400 italic mt-0.5">"{appt.notes}"</p>}
                    </div>
                    <select
                      value={appt.status}
                      onChange={(e) => handleChangeAppointmentStatus(appt, e.target.value)}
                      className="px-2 py-1.5 border rounded-lg text-xs flex-shrink-0"
                    >
                      {Object.entries(APPOINTMENT_STATUS_META).map(([value, m]) => <option key={value} value={value}>{m.label}</option>)}
                    </select>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-3 max-h-[90vh] overflow-y-auto">
            <h3 className="font-bold text-lg text-gray-900">Nuevo calendario</h3>
            <div>
              <label className="text-xs font-semibold text-gray-500">Nombre</label>
              <input
                type="text" autoFocus value={createForm.name}
                onChange={(e) => setCreateForm(f => ({ ...f, name: e.target.value, slug: f.slug || slugify(e.target.value) }))}
                placeholder="Ej. Corte de cabello"
                className="w-full px-3 py-2 border rounded-lg text-sm mt-1"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500">Slug</label>
              <input type="text" value={createForm.slug} onChange={(e) => setCreateForm(f => ({ ...f, slug: e.target.value.toLowerCase() }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500">Empleado dueño de esta agenda</label>
              <select value={createForm.employee_id} onChange={(e) => setCreateForm(f => ({ ...f, employee_id: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-1">
                <option value="">Dueño del negocio</option>
                {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-500">Duración (min)</label>
                <input type="number" min="5" max="480" value={createForm.duration_minutes} onChange={(e) => setCreateForm(f => ({ ...f, duration_minutes: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-1" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500">Buffer (min)</label>
                <input type="number" min="0" max="120" value={createForm.buffer_minutes} onChange={(e) => setCreateForm(f => ({ ...f, buffer_minutes: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-1" />
              </div>
            </div>
            <p className="text-xs text-gray-400">El horario semanal y los bloqueos se configuran después de crear el calendario.</p>
            <div className="flex gap-2 pt-2">
              <button onClick={handleCreateCalendar} disabled={savingCalendar} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                {savingCalendar ? 'Creando...' : 'Crear calendario'}
              </button>
              <button onClick={() => { setShowCreateModal(false); setCreateForm(EMPTY_CALENDAR_FORM); }} className="flex-1 py-2 bg-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-300">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendariosManager;
